import React, { ElementType, useState } from 'react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '@guildeducationinc/catalog-tools';
import { useMemberExperienceAuth } from '@guildeducationinc/auth-frontend-tools';
import { MxButton } from '@guildeducationinc/playground-mx';
import { Dialog, DialogActions, DialogContent, Logo, Typography } from '@guildeducationinc/recess';
import cookies from 'browser-cookies';
import { useQuiz, useQuizDispatch } from '@src/components/Providers/QuizProvider';
import { useRecommendations } from '@src/components/Providers/RecommendationsProvider';
import { hasQuizRecommendations } from '@src/utilities/userUtility';
import styles from './Header.module.scss';

interface Props {
	allowExit?: boolean;
	exitRef?: React.MutableRefObject<HTMLElement>;
}

export const Header: React.FC<Props> = ({ allowExit, exitRef }) => {
	const { userFromIdToken: authUser } = useMemberExperienceAuth();
	const { welcomeSequence } = useQuiz();
	const { dispatch } = useQuizDispatch();
	const { recommendationsData } = useRecommendations();

	const [showDialog, setShowDialog] = useState(false);

	const hasCompletedQuiz = hasQuizRecommendations(recommendationsData);
	const epName = authUser?.employer;
	const epUuid = authUser?.employer_uuid;
	const isMobile = useIsMobile();

	const handleClick = () => {
		cookies.set('hasSeenQuiz', 'true');
		setShowDialog(true);
	};

	return (
		<div className={styles.quizHeader} data-testid='quiz-header' role='navigation'>
			{epName && epUuid && !isMobile && (
				<Logo
					alt={`Guild and ${epName} logos`}
					className={styles.employerLogo}
					cobranded
					uuid={epUuid}
				/>
			)}
			<Logo alt='Guild logo' className={styles.guildLogo} cobranded testid='guild-logo' />
			<div
				className={`${styles.exitButton} ${allowExit ? '' : 'hidden'}`}
				data-testid='exit-button-container'
			>
				<MxButton
					appearance='ghost'
					forwardRef={exitRef as React.Ref<HTMLButtonElement>}
					onClick={handleClick}
				>
					{isMobile ? 'Exit' : 'Exit to catalog'}
				</MxButton>
			</div>
			<Dialog
				isOpen={showDialog}
				onClose={() => setShowDialog(false)}
				size='sm'
				title='Are you sure you want to exit?'
			>
				<DialogContent>
					<Typography variant='body1'>Your progress will not be saved.</Typography>
				</DialogContent>
				<DialogActions fullScreen={false} ref={exitRef}>
					<MxButton
						Component={Link as unknown as ElementType}
						appearance='outline'
						onClick={() =>
							dispatch({
								type: 'updated_screen_history',
								screenHistory: welcomeSequence,
							})
						}
						to={hasCompletedQuiz ? '/allprograms#quiz-results' : '/allprograms'}
					>
						Exit to catalog
					</MxButton>
					<MxButton appearance='primary' onClick={() => setShowDialog(false)}>
						Continue quiz
					</MxButton>
				</DialogActions>
			</Dialog>
		</div>
	);
};
